/**
 * My Apps Page — 내 앱
 *
 * 두 가지 탭:
 * - 내 산출물: 챗봇에서 생성한 모든 HTML 산출물 전체 목록 + 공유 신청 상태
 * - 공유 신청: 공유 신청한 앱의 심사 상태 및 사용 현황
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { Button, Modal, SearchInput, Select, useToast } from '../components/ui';
import SortableTh, { useTableSort } from '../components/ui/SortableTh';
import '../styles/adminPage.css';
import './AppsPage.css';

// ── 공통 타입 ─────────────────────────────────────────────────────────────────

type AppStatus = 'review' | 'change' | 'live' | 'reject' | 'draft';

const STATUS_META: Record<AppStatus, { label: string; bg: string; fg: string }> = {
  review: { label: '검토 중',   bg: '#FDF1DC', fg: '#8A4B00' },
  change: { label: '수정 요청', bg: '#E8F0FC', fg: '#1D4F9C' },
  live:   { label: '게시됨',    bg: '#E6F4EC', fg: '#1E6B44' },
  reject: { label: '반려',      bg: '#FDECEA', fg: '#A8231A' },
  draft:  { label: '임시 저장', bg: '#EEF0F5', fg: '#3D4152' },
};

// ── 내 산출물 타입 & 데이터 ───────────────────────────────────────────────────

type ArtifactType = 'HTML 문서' | '웹앱' | 'HTML 대시보드';

interface Artifact {
  id: string;
  fileName: string;
  type: ArtifactType;
  bot: string;
  botId: string;
  workflowName: string;
  workflowId: string;
  date: string;
  size: string;
  submissionId?: string;
  submissionStatus?: AppStatus;
}

const MY_ARTIFACTS: Artifact[] = [
  { id: 'art1',  fileName: 'aws_cost_anomaly_card.html',       type: 'HTML 문서',     bot: '워킹온 DB 커넥터',    botId: 'wf-cost-001', workflowName: '비용 분석 알림',        workflowId: 'wf-001', date: '10.23 14:02', size: '36KB', submissionId: 'm1', submissionStatus: 'review' },
  { id: 'art2',  fileName: 'ec2_rightsizing_calc.html',        type: '웹앱',          bot: '웹검색',             botId: 'wf-cloud-002', workflowName: 'EC2 최적화 분석',      workflowId: 'wf-002', date: '10.22 10:15', size: '28KB', submissionId: 'm3', submissionStatus: 'live' },
  { id: 'art3',  fileName: 'partner_revenue_q3.html',          type: 'HTML 대시보드', bot: '워킹온 DB 커넥터',    botId: 'wf-cost-001', workflowName: '파트너 매출 집계',      workflowId: 'wf-003', date: '10.21 16:30', size: '42KB', submissionId: 'm2', submissionStatus: 'change' },
  { id: 'art4',  fileName: 'meeting_action_items.html',        type: '웹앱',          bot: '기본 채팅',          botId: 'chat-basic',  workflowName: '회의록 정리기',          workflowId: 'wf-004', date: '10.21 09:00', size: '18KB' },
  { id: 'art5',  fileName: 'support_ticket_form.html',         type: '웹앱',          bot: 'Support-on 가이드', botId: 'wf-spt-003',  workflowName: '장애 접수 자동화',        workflowId: 'wf-005', date: '10.20 14:22', size: '22KB' },
  { id: 'art6',  fileName: 'weekly_it_news_brief.html',        type: 'HTML 문서',     bot: '웹크롤링 워크플로우', botId: 'wf-crawl-01', workflowName: '주간 뉴스 수집·요약',    workflowId: 'wf-006', date: '10.20 08:00', size: '31KB' },
  { id: 'art7',  fileName: 'quarterly_support_report.html',    type: 'HTML 문서',     bot: 'Support-on 가이드', botId: 'wf-spt-003',  workflowName: '분기 리포트 생성',        workflowId: 'wf-007', date: '10.10 11:30', size: '45KB', submissionId: 'm4', submissionStatus: 'live' },
  { id: 'art8',  fileName: 'hmm_project_schedule.html',        type: 'HTML 문서',     bot: '기본 채팅',          botId: 'chat-basic',  workflowName: '프로젝트 일정 관리',      workflowId: 'wf-008', date: '10.19 16:00', size: '22KB', submissionId: 'm6', submissionStatus: 'draft' },
  { id: 'art9',  fileName: 'lunch_recommender.html',           type: '웹앱',          bot: '기본 채팅',          botId: 'chat-basic',  workflowName: '점심 메뉴 추천',          workflowId: 'wf-009', date: '10.15 12:05', size: '18KB', submissionId: 'm5', submissionStatus: 'reject' },
  { id: 'art10', fileName: 'msp_proposal_cover.html',          type: '웹앱',          bot: '기본 채팅',          botId: 'chat-basic',  workflowName: '제안서 표지 생성',        workflowId: 'wf-010', date: '10.17 14:30', size: '15KB' },
  { id: 'art11', fileName: 'aws_cost_monthly_dashboard.html',  type: 'HTML 대시보드', bot: '워킹온 DB 커넥터',    botId: 'wf-cost-001', workflowName: '월간 비용 대시보드',      workflowId: 'wf-011', date: '10.12 09:00', size: '52KB' },
  { id: 'art12', fileName: 'it_infrastructure_review.html',    type: 'HTML 문서',     bot: 'AI DLC 문서 가이드', botId: 'wf-dlc-004',  workflowName: 'IT 인프라 점검 리포트',   workflowId: 'wf-012', date: '10.08 16:45', size: '38KB' },
];

// ── 공유 신청 타입 & 데이터 ───────────────────────────────────────────────────

interface SubmittedApp {
  id: string;
  name: string;
  fileName: string;
  status: AppStatus;
  kind: string;
  scope: string;
  version: string;
  date: string;
  progress: string;
  action: string;
  primary: boolean;
  reviewer?: string;
  comment?: string;
}

const SUBMITTED_APPS: SubmittedApp[] = [
  { id: 'm1', name: 'AWS 비용 이상징후 알림 카드', fileName: 'aws_cost_anomaly_card.html', status: 'review', kind: 'HTML 문서', scope: '에티버스 테넌트', version: 'v1.0', date: '10.23 신청', progress: '고객사 관리자 승인 완료 · 테넌트 관리자 검토 중 (2단계 중 1단계)', action: '신청 내용 보기', primary: false },
  { id: 'm2', name: '파트너 매출(PRM) 집계표', fileName: 'partner_revenue_q3.html', status: 'change', kind: '웹앱', scope: '이테크 시스템', version: 'v1.0', date: '10.22 신청', progress: '수정 후 다시 신청하면 처음 단계부터 검토합니다', action: '수정 후 재신청', primary: true, reviewer: '엄재한 · 고객사 관리자 · 10.22', comment: '고객사별 매출 금액이 화면에 그대로 들어 있어요. 실행하는 사람 권한으로 워킹온 DB에서 조회하도록 데이터 연결을 설정해 주세요.' },
  { id: 'm3', name: 'EC2 Right-sizing 계산기', fileName: 'ec2_rightsizing_calc.html', status: 'live', kind: '웹앱', scope: '에티버스 테넌트', version: 'v1.0', date: '10.18 게시', progress: '157회 실행 · 평점 4.9 (11)', action: '새 버전 올리기', primary: false },
  { id: 'm4', name: '분기 기술지원 리포트', fileName: 'quarterly_support_report.html', status: 'live', kind: 'HTML 문서', scope: '이테크 시스템', version: 'v1.1', date: '10.10 게시', progress: '62회 실행 · 평점 4.5 (6)', action: '새 버전 올리기', primary: false },
  { id: 'm5', name: '사내 점심 메뉴 추천기', fileName: 'lunch_recommender.html', status: 'reject', kind: '웹앱', scope: '에티버스 테넌트', version: 'v1.0', date: '10.16 반려', progress: '나만 보기나 특정 사용자 공유로 바꾸면 계속 쓸 수 있어요', action: '공개 범위 바꾸기', primary: false, reviewer: '어드민 · 테넌트 관리자 · 10.16', comment: '업무 관련성이 낮아 테넌트 전체 공개는 어렵습니다. 팀원 몇 명에게만 공유하는 건 괜찮아요.' },
  { id: 'm6', name: 'HMM 제안 일정표', fileName: 'hmm_project_schedule.html', status: 'draft', kind: 'HTML 문서', scope: '공개 범위 미지정', version: '-', date: '10.19 저장', progress: '앱 정보와 공개 범위를 입력하면 신청할 수 있어요', action: '이어서 작성', primary: true },
];

// ── 산출물 미리보기 목업 ──────────────────────────────────────────────────────

const PREVIEW_MAP: Record<string, () => JSX.Element> = {
  art1: () => (
    <div className="mypv-content">
      <div className="mypv-header"><strong>AWS 비용 이상징후 알림 카드</strong><span>2026-10-23</span></div>
      <div className="mypv-kpi-row">
        {[{ label: '이상 감지', val: '4건', sub: '+50% 초과', bg: '#FDECEA', fg: '#A8231A' }, { label: '최대 급등', val: '+182%', sub: '고객사 A·EC2', bg: '#FDF1DC', fg: '#8A4B00' }, { label: '초과 비용', val: '$3,240', sub: '어제 대비', bg: '#EEECFC', fg: '#3A31A8' }].map(c => (
          <div key={c.label} className="mypv-kpi" style={{ background: c.bg }}><span style={{ color: c.fg, fontSize: 12 }}>{c.label}</span><strong style={{ fontSize: 22, color: c.fg }}>{c.val}</strong><em style={{ fontSize: 12, color: c.fg }}>{c.sub}</em></div>
        ))}
      </div>
      <table className="mypv-table"><thead><tr><th>고객사</th><th>서비스</th><th>증가율</th><th>원인 후보</th></tr></thead>
        <tbody><tr><td>고객사 A</td><td>EC2</td><td style={{color:'#A8231A',fontWeight:700}}>+182%</td><td>예약 인스턴스 만료</td></tr><tr><td>고객사 B</td><td>RDS</td><td style={{color:'#A8231A',fontWeight:700}}>+74%</td><td>스냅샷 백업 주기 변경</td></tr><tr><td>고객사 C</td><td>S3</td><td style={{color:'#8A4B00'}}>+51%</td><td>대용량 파일 업로드</td></tr></tbody>
      </table>
    </div>
  ),
  art2: () => (
    <div className="mypv-content">
      <div className="mypv-header"><strong>EC2 Right-sizing 계산기</strong><span>2026-10-22</span></div>
      <div className="mypv-kpi-row">
        {[{ label: '현재 월 비용', val: '$2,840', sub: '12개 인스턴스', bg: '#EEF0F5', fg: '#3D4152' }, { label: '권장 후 비용', val: '$1,920', sub: '$920 절감', bg: '#E6F4EC', fg: '#1E6B44' }, { label: '절감률', val: '32.4%', sub: '연간 $11,040', bg: '#E6F4EC', fg: '#1E6B44' }].map(c => (
          <div key={c.label} className="mypv-kpi" style={{ background: c.bg }}><span style={{ color: c.fg, fontSize: 12 }}>{c.label}</span><strong style={{ fontSize: 22, color: c.fg }}>{c.val}</strong><em style={{ fontSize: 12, color: c.fg }}>{c.sub}</em></div>
        ))}
      </div>
      <table className="mypv-table"><thead><tr><th>현재 타입</th><th>평균 CPU</th><th>권장 타입</th><th>월 절감</th></tr></thead>
        <tbody><tr><td>m5.2xlarge</td><td>18%</td><td>m5.large</td><td style={{color:'#1E6B44',fontWeight:700}}>$310</td></tr><tr><td>r5.4xlarge</td><td>22%</td><td>r5.xlarge</td><td style={{color:'#1E6B44',fontWeight:700}}>$480</td></tr><tr><td>c5.xlarge</td><td>67%</td><td>c5.xlarge (유지)</td><td>-</td></tr></tbody>
      </table>
    </div>
  ),
};

// 기본 미리보기 (목업 없는 파일)
function DefaultPreview({ art }: { art: Artifact }) {
  return (
    <div className="mypv-content">
      <div className="mypv-header"><strong>{art.fileName}</strong><span>{art.date}</span></div>
      <p style={{ color: 'var(--text-2)', fontSize: 14, margin: 0 }}>{art.bot} 챗봇에서 생성 · {art.type} · {art.size}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '20px', background: 'var(--bg)', borderRadius: 8 }}>
        {[100, 92, 85, 78, 100, 70, 88, 60].map((w, i) => (
          <span key={i} style={{ width: `${w}%`, height: 8, borderRadius: 4, background: i === 0 ? '#9AA0B4' : '#E4E6EF', display: 'block' }} />
        ))}
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-3)', margin: 0 }}>목업 데이터로 표시됩니다.</p>
    </div>
  );
}

// ── 공유 신청 탭 필터 ─────────────────────────────────────────────────────────

type StatusFilter = AppStatus | 'all';

const STATUS_TABS: { id: StatusFilter; label: string }[] = [
  { id: 'all',    label: '전체' },
  { id: 'review', label: '검토 중' },
  { id: 'change', label: '수정 요청' },
  { id: 'live',   label: '게시됨' },
  { id: 'reject', label: '반려' },
  { id: 'draft',  label: '임시 저장' },
];

// ── 눈 아이콘 ─────────────────────────────────────────────────────────────────

function EyeIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export function MyAppsPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  // 메인 탭: 내 산출물 | 공유 신청
  const [mainTab, setMainTab] = useState<'artifacts' | 'submissions'>('artifacts');

  // 내 산출물 탭 상태
  const [artSearch, setArtSearch]       = useState('');
  const [artTypeFilter, setArtTypeFilter] = useState<ArtifactType | 'all'>('all');
  const [previewArt, setPreviewArt]     = useState<Artifact | null>(null);
  const [deleteArtId, setDeleteArtId]   = useState<string | null>(null);
  const [deletedIds,  setDeletedIds]    = useState<Set<string>>(new Set());

  // 정렬 상태 (useTableSort 훅 사용)
  const sort = useTableSort<'fileName' | 'date' | 'type'>();

  // ── 내 산출물 필터링 + 정렬 ──
  const filteredArtifacts = sort.sortRows(
    MY_ARTIFACTS.filter((a) => {
      if (deletedIds.has(a.id)) return false;
      if (artTypeFilter !== 'all' && a.type !== artTypeFilter) return false;
      if (artSearch) {
        const q = artSearch.toLowerCase();
        return a.fileName.toLowerCase().includes(q)
          || a.bot.toLowerCase().includes(q)
          || a.workflowName.toLowerCase().includes(q)
          || a.botId.toLowerCase().includes(q);
      }
      return true;
    }),
    (row, key) => row[key] ?? '',
  );

  // 공유 신청 탭 상태
  const [statusTab, setStatusTab] = useState<StatusFilter>('all');

  // ── 공유 신청 필터링 ──
  const statusCounts = STATUS_TABS.reduce<Record<string, number>>((acc, t) => {
    acc[t.id] = t.id === 'all' ? SUBMITTED_APPS.length : SUBMITTED_APPS.filter(a => a.status === t.id).length;
    return acc;
  }, {});
  const filteredSubmissions = statusTab === 'all' ? SUBMITTED_APPS : SUBMITTED_APPS.filter(a => a.status === statusTab);

  const handleSubmissionAction = (app: SubmittedApp) => {
    if (['review', 'change', 'draft', 'reject'].includes(app.status)) {
      navigate(`/library/my/${app.id}/submit`);
      return;
    }
    showToast('success', `"${app.name}" — ${app.action}`);
  };

  const handleDelete = () => {
    if (!deleteArtId) return;
    setDeletedIds((prev) => new Set([...prev, deleteArtId]));
    const art = MY_ARTIFACTS.find((a) => a.id === deleteArtId);
    setDeleteArtId(null);
    showToast('success', `"${art?.fileName}" 산출물이 삭제됐습니다.`);
  };

  const handleShareRequest = (art: Artifact) => {
    navigate('/library/submit', {
      state: {
        artifact: {
          id:       art.id,
          fileName: art.fileName,
          size:     art.size,
          bot:      art.bot,
          chatDate: art.date,
          type:     art.type,
        },
      },
    });
  };

  // ── 미리보기 렌더 ──
  const renderPreview = () => {
    if (!previewArt) return null;
    const PreviewComp = PREVIEW_MAP[previewArt.id];
    return (
      <div className="mypv-overlay" role="dialog" aria-modal="true" aria-label={`${previewArt.fileName} 미리보기`}>
        <div className="mypv-topbar">
          <div className="mypv-topbar-left">
            <span className="mypv-file-badge">HTML</span>
            <strong>{previewArt.fileName}</strong>
            <span className="mypv-file-meta">{previewArt.type} · {previewArt.size} · {previewArt.date}</span>
          </div>
          <button type="button" className="mypv-close" onClick={() => setPreviewArt(null)} aria-label="닫기">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="mypv-body">
          {PreviewComp ? <PreviewComp /> : <DefaultPreview art={previewArt} />}
        </div>
        <div className="mypv-footer">목업 데이터로 표시됩니다. 실제 산출물은 챗봇 대화에서 확인하세요.</div>
      </div>
    );
  };

  return (
    <>
      {renderPreview()}

      {/* ── 삭제 확인 모달 ── */}
      <Modal
        isOpen={!!deleteArtId}
        onClose={() => setDeleteArtId(null)}
        title="산출물 삭제"
        size="sm"
        style={{ maxWidth: 400 }}
        footerLeft={
          <Button color="gray" appearance="outline" onClick={() => setDeleteArtId(null)}>취소</Button>
        }
        footerRight={
          <Button color="error" onClick={handleDelete}>삭제</Button>
        }
      >
        <p style={{ margin: 0 }}>
          <strong>{MY_ARTIFACTS.find(a => a.id === deleteArtId)?.fileName}</strong> 산출물을 삭제하시겠습니까?
        </p>
        <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-2)' }}>
          삭제된 산출물은 복구할 수 없습니다.
        </p>
      </Modal>

      <Topbar
        title="내가 공유한 항목"
        subtitle="챗봇에서 만든 산출물과 라이브러리 공유 신청 현황을 관리합니다."
      />

      <div className="page-content">
        <div className="page-content-inner apps-container">

          {/* ── 메인 탭 ── */}
          <div className="myapps-main-tabs">
            <button
              type="button"
              className={`myapps-main-tab${mainTab === 'artifacts' ? ' myapps-main-tab--active' : ''}`}
              onClick={() => setMainTab('artifacts')}
            >
              내 산출물
              <span className="myapps-main-tab-count">{MY_ARTIFACTS.length}</span>
            </button>
            <button
              type="button"
              className={`myapps-main-tab${mainTab === 'submissions' ? ' myapps-main-tab--active' : ''}`}
              onClick={() => setMainTab('submissions')}
            >
              공유 신청
              <span className="myapps-main-tab-count">{SUBMITTED_APPS.length}</span>            </button>
          </div>

          {/* ════════════════════════════════
              탭 1: 내 산출물
          ════════════════════════════════ */}
          {mainTab === 'artifacts' && (
            <>
              {/* 검색 + 필터 (오른쪽 정렬) */}
              <div className="admin-panel-actions">
                <Select
                  value={artTypeFilter}
                  onChange={(e) => setArtTypeFilter(e.target.value as ArtifactType | 'all')}
                >
                  <option value="all">유형: 전체</option>
                  <option value="HTML 문서">HTML 문서</option>
                  <option value="웹앱">웹앱</option>
                  <option value="HTML 대시보드">HTML 대시보드</option>
                </Select>
                <SearchInput
                  placeholder="파일명, 워크플로우, 챗봇으로 검색..."
                  value={artSearch}
                  onChange={(e) => setArtSearch(e.target.value)}
                />
              </div>

              {/* 테이블 */}
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th><SortableTh sort={sort} col="fileName">파일명</SortableTh></th>
                      <th><SortableTh sort={sort} col="type">유형</SortableTh></th>
                      <th>워크플로우</th>
                      <th>챗봇</th>
                      <th><SortableTh sort={sort} col="date">생성일시</SortableTh></th>
                      <th>공유 상태</th>
                      <th>작업</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredArtifacts.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="table-empty">검색 결과가 없습니다.</td>
                      </tr>
                    ) : filteredArtifacts.map((art) => {
                      const sm = art.submissionStatus ? STATUS_META[art.submissionStatus] : null;
                      return (
                        <tr key={art.id}>
                          {/* 파일명 */}
                          <td className="left">
                            <button
                              type="button"
                              className="table-link"
                              onClick={() => setPreviewArt(art)}
                              style={{ font: 'inherit' }}
                            >
                              {art.fileName}
                            </button>
                            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{art.size}</div>
                          </td>
                          {/* 유형 */}
                          <td>
                            <span className="myart-type-chip">{art.type}</span>
                          </td>
                          {/* 워크플로우 */}
                          <td className="left">
                            <div style={{ fontSize: 13 }}>{art.workflowName}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>{art.workflowId}</div>
                          </td>
                          {/* 챗봇 */}
                          <td className="left">
                            <div style={{ fontSize: 13 }}>{art.bot}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>{art.botId}</div>
                          </td>
                          {/* 생성일시 */}
                          <td style={{ whiteSpace: 'nowrap', fontSize: 13, color: 'var(--text-2)' }}>{art.date}</td>
                          {/* 공유 상태 */}
                          <td>
                            {sm ? (
                              <span
                                className="myart-status"
                                style={{ background: sm.bg, color: sm.fg, cursor: art.submissionId ? 'pointer' : 'default' }}
                                onClick={() => art.submissionId && navigate(`/library/my/${art.submissionId}/submit`)}
                                role={art.submissionId ? 'button' : undefined}
                                tabIndex={art.submissionId ? 0 : undefined}
                              >
                                {sm.label}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--text-3)', fontSize: 12 }}>미신청</span>
                            )}
                          </td>
                          {/* 작업 */}
                          <td>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <button
                                type="button"
                                className="myapps-preview-btn"
                                onClick={() => setPreviewArt(art)}
                                title="미리보기"
                              >
                                <EyeIcon />
                              </button>
                              {!art.submissionStatus && (
                                <Button size="sm" onClick={() => handleShareRequest(art)}>
                                  공유 신청
                                </Button>
                              )}
                              <button
                                type="button"
                                className="myapps-delete-btn"
                                onClick={() => setDeleteArtId(art.id)}
                                title="삭제"
                                aria-label={`${art.fileName} 삭제`}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                                  <path d="M10 11v6M14 11v6" />
                                  <path d="M9 6V4h6v2" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ════════════════════════════════
              탭 2: 공유 신청
          ════════════════════════════════ */}
          {mainTab === 'submissions' && (
            <>
              {/* 상태 흐름 안내 */}
              <section className="myapps-flow-banner">
                <strong className="myapps-flow-title">상태 흐름</strong>
                {(['draft','review','live'] as AppStatus[]).map((s, i, arr) => (
                  <React.Fragment key={s}>
                    <span className="myapps-status-chip" style={{ background: STATUS_META[s].bg, color: STATUS_META[s].fg }}>{STATUS_META[s].label}</span>
                    {i < arr.length - 1 && <span className="myapps-flow-arrow">→</span>}
                  </React.Fragment>
                ))}
                <span className="myapps-flow-note">검토 중에 수정 요청/반려가 올 수 있어요. 게시 후 새 버전을 올리면 다시 검토를 거칩니다.</span>
              </section>

              {/* 상태 서브탭 */}
              <div className="myapps-tabs" role="tablist">
                {STATUS_TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={statusTab === t.id}
                    className={`myapps-tab${statusTab === t.id ? ' myapps-tab--active' : ''}`}
                    onClick={() => setStatusTab(t.id)}
                  >
                    {t.label} {statusCounts[t.id]}
                  </button>
                ))}
              </div>

              {/* 신청 목록 */}
              <ul className="myapps-list">
                {filteredSubmissions.map((app) => {
                  const sm = STATUS_META[app.status];
                  return (
                    <li key={app.id} className="myapps-row">
                      <div className="myapps-row-main">
                        <div className="myapps-row-info">
                          <div className="myapps-row-title">
                            <strong>{app.name}</strong>
                            <span className="myapps-status-badge" style={{ background: sm.bg, color: sm.fg }}>{sm.label}</span>
                          </div>
                          <span className="myapps-row-meta">{app.kind} · {app.scope} · {app.version} · {app.date}</span>
                        </div>
                        <div className="myapps-row-progress">{app.progress}</div>
                        <div className="myapps-row-action">
                          <button
                            type="button"
                            className="myapps-preview-btn"
                            onClick={() => {
                              const art = MY_ARTIFACTS.find(a => a.submissionId === app.id);
                              if (art) setPreviewArt(art);
                            }}
                            title="산출물 미리보기"
                          >
                            <EyeIcon />
                            미리보기
                          </button>
                          {app.primary ? (
                            <Button size="sm" onClick={() => handleSubmissionAction(app)}>{app.action}</Button>
                          ) : (
                            <Button size="sm" color="gray" appearance="outline" onClick={() => handleSubmissionAction(app)}>{app.action}</Button>
                          )}
                        </div>
                      </div>
                      {app.comment && (
                        <div className="myapps-comment">
                          <div className="myapps-comment-body">
                            <span className="myapps-comment-reviewer">{app.reviewer}</span>
                            <span className="myapps-comment-text">{app.comment}</span>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}

        </div>
      </div>
    </>
  );
}

export default MyAppsPage;
